import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  UserCheck, 
  AlertTriangle, 
  Clock, 
  Calendar,
  Search,
  Filter,
  Cake,
  Star,
  User,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  TrendingUp,
  FileText,
  ChevronRight
} from 'lucide-react';

const API = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");

// Status labels and colors
const STATUS_CONFIG = {
  null: { label: 'Sin revisar', color: 'bg-gray-100 text-gray-700', icon: Clock },
  reviewing: { label: 'En evaluación', color: 'bg-blue-100 text-blue-700', icon: Eye },
  offered: { label: 'Oferta realizada', color: 'bg-purple-100 text-purple-700', icon: FileText },
  signed: { label: 'Firmado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  not_signing: { label: 'No se fichará', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const ALERT_LEVEL_CONFIG = {
  urgent: { label: 'Urgente', color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50 border-red-200' },
  warning: { label: 'Próximo', color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-50 border-amber-200' },
  upcoming: { label: 'Planificación', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50 border-blue-200' }
};

const Signings = () => {
  usePageTitle('Fichajes');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState([]);
  const [allSignings, setAllSignings] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAlertLevel, setFilterAlertLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Summary state
  const [summary, setSummary] = useState({ total: 0, urgent: 0, warning: 0, upcoming: 0 });

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/signings/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
      setSummary(response.data.summary || { total: 0, urgent: 0, warning: 0, upcoming: 0 });
    } catch (error) {
      console.error('Error fetching signing alerts:', error);
      toast.error('Error al cargar alertas de fichajes');
    }
  }, [token]);

  // Fetch all signings (history)
  const fetchAllSignings = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/signings/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSignings(response.data.players || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      console.error('Error fetching all signings:', error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAlerts(), fetchAllSignings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAlerts, fetchAllSignings]);

  // Handle URL params for filtering
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter && ['urgent', 'warning', 'upcoming'].includes(filter)) {
      setFilterAlertLevel(filter);
      setActiveTab('alerts');
    }
  }, [searchParams]);

  // Update status
  const handleUpdateStatus = async () => {
    if (!selectedPlayer || !newStatus) return;
    
    if (newStatus === 'not_signing' && !statusReason.trim()) {
      toast.error('Debe ingresar una razón para no fichar al jugador');
      return;
    }

    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/api/signings/${selectedPlayer.player_id}/status`,
        {
          signing_status: newStatus,
          signing_status_reason: newStatus === 'not_signing' ? statusReason : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Estado actualizado correctamente');
      setShowStatusModal(false);
      setSelectedPlayer(null);
      setNewStatus('');
      setStatusReason('');
      
      // Refresh data
      await Promise.all([fetchAlerts(), fetchAllSignings()]);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Open status modal
  const openStatusModal = (player) => {
    setSelectedPlayer(player);
    setNewStatus(player.signing_status || 'reviewing');
    setStatusReason(player.signing_status_reason || '');
    setShowStatusModal(true);
  };

  // Filter alerts
  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = 
      alert.player_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.category_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLevel = filterAlertLevel === 'all' || alert.alert_level === filterAlertLevel;
    
    return matchesSearch && matchesLevel;
  });

  // Filter history
  const filteredHistory = allSignings.filter(player => {
    const matchesSearch = player.player_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || player.signing_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Navigate to player detail
  const goToPlayer = (playerId) => {
    navigate(`/players/${playerId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-brand-600" />
              Fichajes
            </h1>
            <p className="text-gray-600 mt-1">
              Gestión de jugadores próximos a cumplir 13 años
            </p>
          </div>
          <Button
            onClick={() => Promise.all([fetchAlerts(), fetchAllSignings()])}
            variant="outline"
            data-testid="refresh-signings-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Urgentes</p>
                  <p className="text-2xl font-bold text-red-700">{summary.urgent}</p>
                  <p className="text-xs text-red-500">≤ 7 días</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-600">Próximos</p>
                  <p className="text-2xl font-bold text-amber-700">{summary.warning}</p>
                  <p className="text-xs text-amber-500">8-30 días</p>
                </div>
                <Clock className="w-10 h-10 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Planificación</p>
                  <p className="text-2xl font-bold text-blue-700">{summary.upcoming}</p>
                  <p className="text-xs text-blue-500">31-90 días</p>
                </div>
                <Calendar className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Firmados</p>
                  <p className="text-2xl font-bold text-green-700">{statusCounts.signed || 0}</p>
                  <p className="text-xs text-green-500">Este período</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <Cake className="w-4 h-4" />
              Alertas Activas
              {summary.total > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {summary.total}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Historial
            </TabsTrigger>
          </TabsList>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar jugador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-signings-input"
                />
              </div>
              <Select value={filterAlertLevel} onValueChange={setFilterAlertLevel}>
                <SelectTrigger className="w-48" data-testid="filter-alert-level">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por urgencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las alertas</SelectItem>
                  <SelectItem value="urgent">🔴 Urgentes (≤7 días)</SelectItem>
                  <SelectItem value="warning">🟡 Próximos (≤30 días)</SelectItem>
                  <SelectItem value="upcoming">🔵 Planificación (31-90 días)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Alerts List */}
            {filteredAlerts.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-4" />
                  <p className="text-gray-600">No hay alertas de fichaje pendientes</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Todos los jugadores próximos a cumplir 13 años han sido procesados
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredAlerts.map((alert) => {
                  const alertConfig = ALERT_LEVEL_CONFIG[alert.alert_level];
                  const statusConfig = STATUS_CONFIG[alert.signing_status];
                  const StatusIcon = statusConfig?.icon || Clock;
                  
                  return (
                    <Card 
                      key={alert.player_id} 
                      className={`border ${alertConfig.bgLight} hover:shadow-md transition-shadow`}
                      data-testid={`signing-alert-${alert.player_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          {/* Player Info */}
                          <div className="flex items-center gap-4">
                            <div className={`w-2 h-16 rounded ${alertConfig.color}`} />
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 
                                  className="font-semibold text-gray-900 hover:text-brand-600 cursor-pointer"
                                  onClick={() => goToPlayer(alert.player_id)}
                                >
                                  {alert.player_name}
                                </h3>
                                {alert.overall_score && (
                                  <span className="flex items-center gap-1 text-sm text-amber-600">
                                    <Star className="w-3 h-3 fill-amber-400" />
                                    {alert.overall_score.toFixed(0)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {alert.category_name} • {alert.position || 'Sin posición'}
                              </p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Cake className="w-3 h-3" />
                                  Cumple 13 el {(() => {
                                    const [y, m, d] = alert.turns_13_on.split('-').map(Number);
                                    return new Date(y, m - 1, d).toLocaleDateString('es-UY');
                                  })()}
                                </span>
                                <span className={`font-semibold ${alertConfig.textColor}`}>
                                  {alert.days_until_13} días restantes
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Status and Actions */}
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig?.color || 'bg-gray-100 text-gray-700'}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig?.label || 'Sin revisar'}
                            </span>
                            <Button
                              size="sm"
                              onClick={() => openStatusModal(alert)}
                              data-testid={`update-status-${alert.player_id}`}
                            >
                              Actualizar
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar jugador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="reviewing">🔵 En evaluación ({statusCounts.reviewing || 0})</SelectItem>
                  <SelectItem value="offered">🟣 Oferta realizada ({statusCounts.offered || 0})</SelectItem>
                  <SelectItem value="signed">🟢 Firmados ({statusCounts.signed || 0})</SelectItem>
                  <SelectItem value="not_signing">🔴 No fichados ({statusCounts.not_signing || 0})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* History List */}
            {filteredHistory.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No hay registros de fichajes</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Los jugadores procesados aparecerán aquí
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredHistory.map((player) => {
                  const statusConfig = STATUS_CONFIG[player.signing_status];
                  const StatusIcon = statusConfig?.icon || Clock;
                  
                  return (
                    <Card key={player.player_id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          {/* Player Info */}
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${statusConfig?.color?.replace('text-', 'bg-').replace('-700', '-100') || 'bg-gray-100'}`}>
                              <StatusIcon className={`w-5 h-5 ${statusConfig?.color?.split(' ')[1] || 'text-gray-600'}`} />
                            </div>
                            <div>
                              <h3 
                                className="font-semibold text-gray-900 hover:text-brand-600 cursor-pointer"
                                onClick={() => goToPlayer(player.player_id)}
                              >
                                {player.player_name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {player.category_name} • {player.position || 'Sin posición'}
                              </p>
                              {player.signing_status === 'not_signing' && player.signing_status_reason && (
                                <p className="text-xs text-red-600 mt-1">
                                  Razón: {player.signing_status_reason}
                                </p>
                              )}
                              {player.signing_status_updated_at && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Actualizado: {new Date(player.signing_status_updated_at).toLocaleDateString('es-UY')} por {player.signing_status_updated_by}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Status */}
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig?.color || 'bg-gray-100 text-gray-700'}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig?.label || 'Sin revisar'}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openStatusModal(player)}
                            >
                              Editar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Status Update Modal */}
        <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Actualizar Estado de Fichaje</DialogTitle>
              <DialogDescription>
                {selectedPlayer?.player_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reviewing">
                      <span className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-blue-500" />
                        En evaluación
                      </span>
                    </SelectItem>
                    <SelectItem value="offered">
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-500" />
                        Oferta realizada
                      </span>
                    </SelectItem>
                    <SelectItem value="signed">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Firmado
                      </span>
                    </SelectItem>
                    <SelectItem value="not_signing">
                      <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        No se fichará
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newStatus === 'not_signing' && (
                <div className="space-y-2">
                  <Label>Razón <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    placeholder="Ingrese la razón por la que no se fichará al jugador..."
                    rows={3}
                    data-testid="status-reason-input"
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowStatusModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateStatus}
                disabled={updatingStatus || !newStatus}
                data-testid="confirm-status-update"
              >
                {updatingStatus ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Signings;
